import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';

// Only interface preferences live here; account-scoped, never part of a plan.
class ViewCache extends Map<string, unknown> {
  private timer: ReturnType<typeof setTimeout> | undefined;
  constructor(private storageKey: string) {
    super();
    try { const entries = JSON.parse(localStorage.getItem(storageKey) ?? '[]', (_,value) => value?.__scrollMap ? new Map(value.__scrollMap) : value); if (Array.isArray(entries)) for (const [key,value] of entries) super.set(key,value); } catch { /* Ignore invalid local preferences. */ }
  }
  override set(key: string, value: unknown) {
    super.set(key,value);
    clearTimeout(this.timer); this.timer=setTimeout(()=>this.flush(),250);
    return this;
  }
  flush() {
    clearTimeout(this.timer);
    try { localStorage.setItem(this.storageKey, JSON.stringify([...this], (_,value)=>value instanceof Map ? {__scrollMap:[...value]} : value)); } catch { /* Full or disabled local storage must not block editing. */ }
  }
}
const StateContext = createContext<Map<string, unknown> | null>(null);
const ScopeContext = createContext('');
export function ViewStateProvider({ accountKey, children }: { accountKey: string; children: ReactNode }) {
  const cache = useMemo(() => new ViewCache(`minglun.ui.v2.${accountKey}`), [accountKey]);
  useEffect(()=>{ const flush=()=>cache.flush(); window.addEventListener('pagehide',flush); return()=>{flush();window.removeEventListener('pagehide',flush);}; },[cache]);
  return <StateContext.Provider value={cache}>{children}</StateContext.Provider>;
}
export function ViewStateScope({ scope, children }: { scope: string; children: ReactNode }) {
  const parent = useContext(ScopeContext);
  return <ScopeContext.Provider value={`${parent}/${scope}`}>{children}</ScopeContext.Provider>;
}
export function useViewState<T>(name: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const cache = useContext(StateContext);
  const key = `${useContext(ScopeContext)}/${name}`;
  const create = (): T => cache?.has(key) ? cache.get(key) as T : typeof initial === 'function' ? (initial as () => T)() : initial;
  const [slot, setSlot] = useState(() => ({ key, value: create() }));
  if (slot.key !== key) setSlot({ key, value: create() });
  const value = slot.key === key ? slot.value : create();
  const update: Dispatch<SetStateAction<T>> = next => {
    setSlot(current => {
      const previous = current.key === key ? current.value : create();
      const value = typeof next === 'function' ? (next as (value: T) => T)(previous) : next;
      cache?.set(key, value);
      if (current.key === key && Object.is(current.value, value)) return current;
      return { key, value };
    });
  };
  return [value, update];
}

export function PageViewport({ name, children }: { name: string; children: ReactNode }) {
  const cache = useContext(StateContext);
  const root = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const key = `scroll:${name}`;
    const positions = new Map(cache?.get(key) as Map<string, number> | undefined);
    const identify = (target: HTMLElement) => target === element ? '$' : target.dataset.scrollId;
    let frame = 0;
    const restore = () => {
      [element, ...Array.from(element.querySelectorAll<HTMLElement>('[data-scroll-id]'))].forEach(target => {
        const id = identify(target);
        if (!id) return;
        const top = positions.get(id);
        if (top !== undefined && target.scrollHeight - target.clientHeight >= top) {
          target.scrollTop = top;
          positions.delete(id);
        }
      });
      if (!positions.size) observer.disconnect();
    };
    const observer = new MutationObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(restore); });
    if (positions.size) observer.observe(element, { childList: true, subtree: true });
    frame = requestAnimationFrame(restore);
    const stopRestore = () => { positions.clear(); observer.disconnect(); cancelAnimationFrame(frame); };
    const onScroll = (event: Event) => {
      if (!(event.target instanceof HTMLElement)) return;
      const id = identify(event.target);
      if (!id || positions.has(id)) return;
      let stored = cache?.get(key) as Map<string, number> | undefined;
      if (!stored) { stored = new Map(); cache?.set(key, stored); }
      stored.set(id, event.target.scrollTop);
      cache?.set(key, stored);
    };
    element.addEventListener('scroll', onScroll, true);
    element.addEventListener('wheel', stopRestore, { passive: true });
    element.addEventListener('touchstart', stopRestore, { passive: true });
    element.addEventListener('keydown', stopRestore);
    return () => {
      stopRestore();
      element.removeEventListener('scroll', onScroll, true);
      element.removeEventListener('wheel', stopRestore);
      element.removeEventListener('touchstart', stopRestore);
      element.removeEventListener('keydown', stopRestore);
    };
  }, [cache, name]);
  return <div ref={root} className="page-motion" data-page={name.split(':')[0]}>{children}</div>;
}

export function useCompactScreen() {
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 760px), (max-width: 1000px) and (max-height: 500px)').matches);
  useLayoutEffect(() => {
    const query = window.matchMedia('(max-width: 760px), (max-width: 1000px) and (max-height: 500px)');
    const update = () => setCompact(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return compact;
}

/** A portal's list has its own scroll owner, outside PageViewport. */
export function RememberedScroll({name,className,children}:{name:string;className:string;children:ReactNode}) {
  const {root,remember}=useRememberedScroll(`portal-scroll:${name}`);
  return <div ref={root} className={className} data-scroll-id={name} onScroll={remember}>{children}</div>;
}

/** Scroll memory never needs React to render on each scroll event. */
export function useRememberedScroll(name:string) {
  const cache=useContext(StateContext);
  const key=`${useContext(ScopeContext)}/${name}`;
  const root=useRef<HTMLDivElement>(null);
  useLayoutEffect(()=>{if(root.current)root.current.scrollTop=(cache?.get(key) as number)??0;},[cache,key]);
  return {root,remember:()=>{if(root.current)cache?.set(key,root.current.scrollTop);}};
}
